import { faAngleDown, faSpinner, faChevronDown, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const myDebounce = (call, inputValue, d) => {
  let timer;
  return function (...args) {
    if (timer) clearTimeout(timer);
    setTimeout(() => {
      call(inputValue);
    }, d);
  };
};

export const BetterDebounce = (someFunc, inputValue) =>
  myDebounce(someFunc, inputValue, 1000);

const myThrottle = (fn, d, buttonRef) => {
  let isThrottled = false;
  return function (...args) {
    if (buttonRef) {
      if (buttonRef.current) {
        buttonRef.current.disabled = true;
      }
    }
    if (!isThrottled) {
      isThrottled = true;
      setTimeout(() => {
        if (buttonRef) {
          if (buttonRef.current) {
            buttonRef.current.disabled = false;
          }
        }
        fn(...args);
        isThrottled = false;
      }, d);
    }
  };
};

export const BetterThrottle = (call, d, buttonRef) =>
  myThrottle(
    () => {
      call();
    },
    d,
    buttonRef
  );

export const SearchInput = ({
  onSearch,
  filterOptions = [],
  setToSearchBasedOnField,
}) => {
  const location = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [clear, setClear] = useState(false);
  const buttonRef = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState("Search By Field...");

  // Keep a ref to the latest onSearch so the debounced invoker (created once)
  // always calls the current callback without needing to be re-created.
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Stable debounced invoker: created once so its timer persists across renders.
  // Typing fires onSearch after the debounce window (matches the quote-list search).
  const debouncedSearchRef = useRef(null);
  if (!debouncedSearchRef.current) {
    let timer;
    debouncedSearchRef.current = (value) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        onSearchRef.current(value);
      }, 350);
    };
  }

  const handleDropdownClick = () => {
    setShowOptions(!showOptions);
  };

  const handleSearchChange = (e) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);
    debouncedSearchRef.current(inputValue);
  };

  const handleButtonClick = BetterThrottle(
    (e) => {
      onSearch(searchInput);
      setBtnDisabled(false);
      setSearchInProgress(true);
      setTimeout(() => {
        setSearchInProgress(false);
      }, 1000);
    },
    2000,
    buttonRef
  );

  // const changeFilterOptions = () => {
  //   return filterOptions.map((data) => {
  //     if (data == "All") {
  //       return { name: data, val: "" };
  //     } else return { name: data, val: data };
  //   });
  // };

  const handleOptionChange = (optionKey, optionValue) => {
    setSelectedOption(optionKey);
    setToSearchBasedOnField(optionValue);
    setShowOptions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  useEffect(() => {
    if (clear) {
      handleButtonClick(searchInput);
    }
  }, [clear, searchInput, handleButtonClick]);

  const shouldShowFieldDropdown = !(
    location.pathname === "/admin/users/content" ||
    location.pathname === "/user/dashboard"
  );

  return (
    <div className="flex items-center gap-2">
      {shouldShowFieldDropdown && (
        <div className="relative" ref={dropdownRef}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleDropdownClick}
            className="cursor-pointer rounded-lg px-4 py-2.5 flex items-center justify-between min-w-[160px] bg-white/80 dark:bg-[oklch(0.26_0.028_264/0.6)] backdrop-blur-[8px] border border-blue-500/20 dark:border-blue-500/30 hover:border-blue-500/30 dark:hover:border-blue-500/40"
            style={{
              WebkitBackdropFilter: 'blur(8px)',
              fontFamily: 'inherit'
            }}
          >
            <span
              className="text-sm font-medium truncate text-gray-900 dark:text-gray-100"
              style={{
                fontFamily: 'inherit'
              }}
            >
              {selectedOption}
            </span>
            <motion.div
              animate={{ rotate: showOptions ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ml-2"
            >
              <FontAwesomeIcon
                icon={faChevronDown}
                className="text-blue-500 dark:text-blue-400"
                style={{
                  fontSize: '12px'
                }}
              />
            </motion.div>
          </motion.div>

          <AnimatePresence>
            {showOptions && Object.keys(filterOptions).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 mt-2 rounded-lg shadow-lg z-[100] min-w-[160px] bg-white/95 dark:bg-[oklch(0.26_0.028_264/0.95)] backdrop-blur-[12px] border border-gray-200 dark:border-white/10"
                style={{
                  WebkitBackdropFilter: 'blur(12px)'
                }}
              >
                {Object.keys(filterOptions).map((option, index) => (
                  <motion.div
                    key={index}
                    className={`hover:bg-blue-500/10 dark:hover:bg-blue-500/20 px-4 py-2.5 cursor-pointer transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg text-gray-900 dark:text-gray-100 ${index < Object.keys(filterOptions).length - 1
                        ? 'border-b border-gray-200/50 dark:border-white/5'
                        : ''
                      }`}
                    whileHover={{}}
                    onClick={() => handleOptionChange(option, filterOptions[option])}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    {option}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {shouldShowFieldDropdown && (
        <div
          className="w-[1px] h-8 bg-gray-300/20 dark:bg-white/20"
        />
      )}

      <div className="relative flex items-center flex-1 max-w-[500px]">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            handleSearchChange(e);
            if (e.target.value === "") {
              onSearch(e.target.value);
            }
          }}
          placeholder="Search...."
          className="w-full px-4 py-2.5 pr-10 rounded-lg focus:outline-none transition-all duration-200 search-input-glass bg-white/80 dark:bg-[oklch(0.26_0.028_264/0.6)] backdrop-blur-[8px] border border-blue-500/20 dark:border-blue-500/30 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500/60 dark:focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-500/20"
          style={{
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: 'inherit',
            fontSize: '14px'
          }}
        />
        <motion.div
          role="button"
          aria-label="Search"
          tabIndex={0}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="absolute right-3 cursor-pointer"
          onClick={(e) => {
            setBtnDisabled(true);
            if (!btnDisabled) {
              handleButtonClick(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setBtnDisabled(true);
              if (!btnDisabled) {
                handleButtonClick(e.target.value);
              }
            }
          }}
        >
          <FontAwesomeIcon
            icon={faSearch}
            className="text-blue-500 dark:text-blue-400"
            style={{
              fontSize: '16px'
            }}
          />
        </motion.div>
      </div>

      {searchInProgress && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="text-blue-500 dark:text-blue-400"
            style={{
              fontSize: '18px'
            }}
          />
        </motion.div>
      )}
    </div>
  );
};
export const FilterDropdown = ({ options, onSelect, input }) => {
  return (
    <div className="flex md:mx-5  w-full">
      <select
        onChange={(e) => onSelect(e.target.value)}
        className="block w-64 text-black md:px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500"
      >
        <option value="">{input ? input : "Select Filter Value"}</option>
        {options.map((option, index) => (
          <option key={index} value={option.val}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
};
